<template>
  <div>
    <!-- Adicionar usuário do sistema -->
    <div class="mb-4">
      <strong class="d-block mb-2">Adicionar usuário do sistema</strong>
      <SGPFormAutocompleteComponent
        name="adicionar_usuario"
        :listaDeOptions="usuariosDisponiveis"
        optionLabel="rotulo"
        optionValue="id"
        placeholder="Buscar por nome ou e-mail"
        v-model="usuarioSelecionado"
        :limpavel="true"
        :loadingProp="loadingUsuarios"
        @change="adicionarUsuario"
      />
    </div>

    <!-- Adicionar destinatário dinâmico -->
    <div v-if="variaveisDeDestinatario.length" class="mb-4">
      <strong class="d-block mb-2">Adicionar destinatário dinâmico</strong>
      <div class="variaveis-destinatario-lista">
        <button
          v-for="v in variaveisDeDestinatario"
          :key="v.key"
          type="button"
          :class="['variavel-dest-btn', { 'variavel-dest-btn--adicionado': jaAdicionado(v.key) }]"
          :disabled="jaAdicionado(v.key)"
          :title="v.description"
          @click="adicionarDinamico(v)"
        >
          <SGPIconComponent icon="mdi-lightning-bolt" class="mr-1" />
          <span>{{ v.label }}</span>
          <small class="text-muted ml-1">({{ variavelLabel(v.key) }})</small>
        </button>
      </div>
      <small class="text-muted d-block mt-1">
        O e-mail será resolvido dinamicamente no momento do envio.
      </small>
    </div>

    <!-- Adicionar destinatário dinâmico por variável customizada -->
    <div class="mb-4">
      <button
        type="button"
        class="v-btn v-btn--text theme--light v-size--small grey--text text-capitalize px-0"
        @click="mostrarVariavelCustom = !mostrarVariavelCustom"
      >
        <SGPIconComponent :icon="mostrarVariavelCustom ? 'mdi-chevron-up' : 'mdi-chevron-down'" class="mr-1" />
        Adicionar destinatário dinâmico
      </button>

      <div v-if="mostrarVariavelCustom" class="mt-2">
        <div class="row no-gutters align-items-center">
          <div class="col pr-2">
            <SGPFormInputComponent
              type="text"
              name="dest_chave"
              v-model="chaveVariavel"
              placeholder="nome_da_variavel"
              :error="erroChaveVariavel"
            />
          </div>
          <div v-if="chaveVariavelNormalizada" class="col-auto pr-2">
            <code class="dest-chave-preview">{{ variavelLabel(chaveVariavelNormalizada) }}</code>
          </div>
          <div class="col-auto">
            <button
              type="button"
              class="v-btn v-btn--has-bg v-btn--rounded theme--light v-size--small primary text-capitalize"
              :disabled="!chaveVariavelNormalizada"
              @click="adicionarPorChave"
            >
              + Add
            </button>
          </div>
        </div>
        <small class="text-muted d-block mt-1" style="font-size:11px">
          Digite apenas a chave (ex: <code>gestor_email</code>). O e-mail será resolvido pelo backend em tempo de execução.
        </small>
      </div>
    </div>

    <!-- Adicionar destinatário livre (colapsável) -->
    <div class="mb-4">
      <button
        type="button"
        class="v-btn v-btn--text theme--light v-size--small grey--text text-capitalize px-0"
        @click="mostrarLivre = !mostrarLivre"
      >
        <SGPIconComponent :icon="mostrarLivre ? 'mdi-chevron-up' : 'mdi-chevron-down'" class="mr-1" />
        Adicionar destinatário externo (e-mail avulso)
      </button>

      <div v-if="mostrarLivre" class="mt-2">
        <div class="row no-gutters align-items-start">
          <div class="col pr-2">
            <SGPFormInputComponent
              type="text"
              name="dest_livre_nome"
              v-model="livraNome"
              placeholder="Nome (opcional)"
            />
          </div>
          <div class="col pr-2">
            <SGPFormInputComponent
              type="text"
              name="dest_livre_email"
              v-model="livreEmail"
              placeholder="email@exemplo.com"
              :error="erroLivreEmail"
            />
          </div>
          <div class="col-auto pt-1">
            <button
              type="button"
              class="v-btn v-btn--has-bg v-btn--rounded theme--light v-size--small primary text-capitalize"
              :disabled="!livreEmail"
              @click="adicionarLivre"
            >
              + Add
            </button>
          </div>
        </div>

      </div>
    </div>

    <v-divider class="mb-3" />

    <!-- Lista configurada -->
    <strong class="d-block mb-2">
      Destinatários configurados
      <span class="text-muted font-weight-regular">({{ lista.length }})</span>
    </strong>

    <p v-if="!lista.length" class="text-muted small mb-3">
      Nenhum destinatário configurado ainda.
    </p>

    <ul class="list-unstyled destinatarios-lista mb-4">
      <li
        v-for="(dest, index) in lista"
        :key="`dest-${index}`"
        :class="['destinatario-item d-flex align-items-center justify-content-between', 'dest--' + classifyRecipient(dest)]"
      >
        <!-- Normal (usuário ou e-mail fixo) -->
        <div v-if="classifyRecipient(dest) === 'normal'">
          <SGPIconComponent
            :icon="dest.user_id ? 'mdi-account' : 'mdi-email-outline'"
            class="mr-2"
          />
          <span>{{ rotulo(dest) }}</span>
          <small v-if="dest.user_id" class="text-muted ml-2">Usuário do sistema</small>
          <small v-else class="text-muted ml-2">Externo</small>
        </div>

        <!-- Dinâmico — variável reconhecida -->
        <div v-else-if="classifyRecipient(dest) === 'dynamic-ok'">
          <SGPIconComponent icon="mdi-lightning-bolt" class="mr-2 success--text" />
          <span>{{ labelDaVariavelDinamica(dest.email) }}</span>
          <span class="dest-badge dest-badge--dinamico ml-2">Dinâmico</span>
        </div>

        <!-- Dinâmico — variável desconhecida -->
        <div v-else>
          <SGPIconComponent icon="mdi-alert" class="mr-2 warning--text" />
          <span>{{ dest.email }}</span>
          <span class="dest-badge dest-badge--pendente ml-2">Pendente</span>
          <small class="text-muted d-block" style="font-size:11px;margin-top:2px">
            Variável não implementada no backend — o e-mail não será enviado
          </small>
        </div>

        <button
          type="button"
          class="v-btn v-btn--text v-btn--icon theme--light v-size--small"
          title="Remover"
          @click="remover(index)"
        >
          <SGPIconComponent icon="mdi-delete" />
        </button>
      </li>
    </ul>

    <!-- Ações do modal -->
    <div class="container-botoes">
      <button
        type="button"
        class="v-btn v-btn--has-bg v-btn--rounded theme--light v-size--default primary text-capitalize"
        :disabled="salvando"
        @click="salvar"
      >
        Salvar destinatários
      </button>
      <button
        type="button"
        class="v-btn v-btn--outlined v-btn--rounded theme--light v-size--default grey-5--text text-capitalize"
        @click="$emit('fechar')"
      >
        Cancelar
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { formatarDestinatario, sanitizarDestinatariosParaPayload } from '../helpers/destinatarios'

const VALIDADOR_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
  name: 'DestinatariosModalComponent',

  props: {
    recipients: {
      type: Array,
      default: () => []
    },
    salvando: {
      type: Boolean,
      default: false
    },
    availableVariables: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      lista: this.normalizar(this.recipients),
      usuarioSelecionado: null,
      livraNome: '',
      livreEmail: '',
      erroLivreEmail: '',
      mostrarLivre: false,
      chaveVariavel: '',
      erroChaveVariavel: '',
      mostrarVariavelCustom: false,
      loadingUsuarios: false
    }
  },

  computed: {
    ...mapGetters('usuarios', ['listaDeUsuarios']),

    variaveisDeDestinatario () {
      return (this.availableVariables || []).filter(v => {
        const scope = v.scope || 'body'
        return scope === 'recipient' || scope === 'both'
      })
    },

    chaveVariavelNormalizada () {
      return (this.chaveVariavel || '').trim().replace(/[^\w]/g, '_').replace(/^_+|_+$/g, '') || ''
    },

    usuariosDisponiveis () {
      const idsSelecionados = new Set(
        this.lista.filter(d => d.user_id).map(d => Number(d.user_id))
      )
      return (this.listaDeUsuarios || [])
        .filter(u => !idsSelecionados.has(Number(u.id)))
        .map(u => ({
          id: u.id,
          rotulo: u.email ? `${u.name} (${u.email})` : u.name
        }))
    }
  },

  watch: {
    recipients (novo) {
      this.lista = this.normalizar(novo)
    }
  },

  async created () {
    if (!this.listaDeUsuarios || this.listaDeUsuarios.length === 0) {
      this.loadingUsuarios = true
      try {
        await this.listarUsuarios()
      } finally {
        this.loadingUsuarios = false
      }
    }
  },

  methods: {
    ...mapActions('usuarios', ['listarUsuarios']),

    normalizar (input) {
      if (!Array.isArray(input)) return []
      return input.map(d => ({
        user_id: d.user_id ?? null,
        name: d.name ?? null,
        email: d.email ?? null,
        user: d.user ?? null
      }))
    },

    rotulo (dest) {
      return formatarDestinatario(dest)
    },

    adicionarUsuario (userId) {
      if (!userId) return
      const u = (this.listaDeUsuarios || []).find(x => Number(x.id) === Number(userId))
      if (!u) return
      if (this.lista.some(d => d.user_id && Number(d.user_id) === Number(userId))) {
        this.usuarioSelecionado = null
        return
      }
      this.lista.push({
        user_id: Number(userId),
        name: null,
        email: null,
        user: { id: u.id, name: u.name, email: u.email }
      })
      this.usuarioSelecionado = null
    },

    adicionarLivre () {
      const email = (this.livreEmail || '').trim()
      if (!email) {
        this.erroLivreEmail = 'Informe um e-mail.'
        return
      }
      const isDinamico = /^\{\{\w+\}\}$/.test(email)
      if (!isDinamico && !VALIDADOR_EMAIL.test(email)) {
        this.erroLivreEmail = 'E-mail inválido.'
        return
      }
      if (this.lista.some(d => !d.user_id && (d.email || '').toLowerCase() === email.toLowerCase())) {
        this.erroLivreEmail = 'Este e-mail já foi adicionado.'
        return
      }
      this.lista.push({
        user_id: null,
        name: (this.livraNome || '').trim() || null,
        email,
        user: null
      })
      this.livraNome = ''
      this.livreEmail = ''
      this.erroLivreEmail = ''
    },

    variavelLabel (key) {
      return `{{${key}}}`
    },

    adicionarPorChave () {
      const key = this.chaveVariavelNormalizada
      if (!key) return
      const email = `{{${key}}}`
      if (this.lista.some(d => d.email === email)) {
        this.erroChaveVariavel = 'Esta variável já foi adicionada.'
        return
      }
      const varConhecida = (this.availableVariables || []).find(v => v.key === key)
      this.lista.push({
        user_id: null,
        name: varConhecida ? varConhecida.label : key,
        email,
        user: null
      })
      this.chaveVariavel = ''
      this.erroChaveVariavel = ''
    },

    adicionarDinamico (v) {
      const email = `{{${v.key}}}`
      if (this.lista.some(d => d.email === email)) return
      this.lista.push({
        user_id: null,
        name: v.label || v.key,
        email,
        user: null
      })
    },

    jaAdicionado (key) {
      return this.lista.some(d => d.email === `{{${key}}}`)
    },

    classifyRecipient (dest) {
      if (!dest.email || !dest.email.match(/^\{\{.+\}\}$/)) return 'normal'
      const key = dest.email.replace(/\{\{|\}\}/g, '')
      const exists = (this.availableVariables || []).some(
        v => v.key === key && (v.scope === 'recipient' || v.scope === 'both')
      )
      return exists ? 'dynamic-ok' : 'dynamic-pending'
    },

    labelDaVariavelDinamica (email) {
      const key = (email || '').replace(/\{\{|\}\}/g, '')
      const v = (this.availableVariables || []).find(x => x.key === key)
      return v ? v.label : key
    },

    remover (index) {
      this.lista.splice(index, 1)
    },

    salvar () {
      this.$emit('salvar', sanitizarDestinatariosParaPayload(this.lista))
    }
  }
}
</script>

<style scoped>
.destinatarios-lista {
  max-height: 300px;
  overflow-y: auto;
  margin: 0;
  padding: 0;
}

.destinatario-item {
  background-color: #fafafa;
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 6px;
}

.dest--dynamic-ok {
  background-color: #f1f8e9;
  border-color: #c5e1a5;
}

.dest--dynamic-pending {
  background-color: #fff8e1;
  border-color: #ffe082;
}

.dest-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  border-radius: 3px;
  padding: 1px 5px;
  vertical-align: middle;
}

.dest-badge--dinamico {
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
}

.dest-badge--pendente {
  background: #fff9c4;
  color: #f57f17;
  border: 1px solid #ffe082;
}

.variaveis-destinatario-lista {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.variavel-dest-btn {
  display: inline-flex;
  align-items: center;
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s;
}

.variavel-dest-btn:hover:not(:disabled) {
  background: #c8e6c9;
}

.variavel-dest-btn--adicionado {
  opacity: 0.5;
  cursor: default;
}

.dest-dica-dinamico {
  font-size: 12px;
  color: #666;
  background: #fffde7;
  border: 1px solid #fff176;
  border-radius: 4px;
  padding: 6px 10px;
}

.dest-chave-preview {
  font-size: 13px;
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  border-radius: 4px;
  padding: 4px 8px;
  color: #2e7d32;
  white-space: nowrap;
}
</style>
